import React from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import { Button, Space, Typography } from 'antd';
import { resolveThinkingProcessResourceEntries, resolveThinkingTool } from './thinkingState.js';
import { resolveThinkingProcessActivityEntries } from './thinkingProcessActivityProjection.js';
import ThinkingProcessLog, { ProcessCompactSummary } from './ThinkingProcessLog.jsx';
import MessageIdentityAvatar from './MessageIdentityAvatar.jsx';
import { resolveBusinessProcessSummary, resolveProcessScope, resolveSettlementState, resolveStatusPresentation, resolveStatusStyleKey, resolveWorkflowState, shouldOpenThinkingProcess } from "./thinkingCardPresentation.jsx";
import { resolveProcessEntryPresentation } from "./assistantTurnPresentation.jsx";
export { shouldOpenThinkingProcess, shouldAnimateThinkingProcess } from './thinkingCardPresentation.jsx';
const { Text } = Typography;
const conditionalValue = (condition, whenTrue, whenFalse) => condition ? whenTrue : whenFalse;

export function ThinkingCard(props) {
  const {
    item,
    showIdentity,
    assistantName,
    renderMessageAvatar,
    hasStreamingContent,
    historical,
    onRegenerate,
    onUICardAction,
    onWorkflowOperationAction,
    regenerateEnabled,
    skillProjection,
    themeTokens,
  } = { hasStreamingContent: false, historical: false, regenerateEnabled: false, showIdentity: true, ...props };
  const workflowState = resolveWorkflowState(item, hasStreamingContent);
  const {
    hasBusinessProgress,
    hasTrackResult,
    hasWorkflowTracker,
    progress,
    settlementVisible,
    stageLabel,
    trackResultProcessEntries,
    trackerProcessEntries,
    workflowTracker,
  } = workflowState;
  const settlement = resolveSettlementState(item, settlementVisible);
  const tool = resolveThinkingTool(item);
  const processScopeEntries = resolveProcessScope({
    item,
    hasWorkflowTracker,
    trackerProcessEntries,
    hasTrackResult,
    trackResultProcessEntries,
  });
  const processLines = processScopeEntries.map(({ line }) => line);
  const resourceEntries = resolveThinkingProcessResourceEntries(item);
  const activityEntries = resolveThinkingProcessActivityEntries(item, skillProjection);
  const workflowProcessEntries = resolveProcessEntryPresentation({ item, entries: processScopeEntries, skillProjection });
  const hasProcessLines = processLines.length > 0;
  const processSummary = resolveBusinessProcessSummary({
    hasBusinessProgress,
    hasWorkflowTracker,
    hasTrackResult,
    processLines,
    progress,
    workflowTracker,
    trackResult: workflowState.trackResult,
  });
  const shouldOpenProcess = shouldOpenThinkingProcess({
    completed: settlement.completed,
    hasProcessLines,
    waitingUser: settlement.waitingUser,
  });
  const visibleProcessLines = processLines;
  const status = resolveStatusPresentation({
    item,
    workflowTracker,
    workflowState,
    settlement,
    tool,
    historical,
    hasStreamingContent,
  });
  const {
    dimmed, isSettled, secondaryLabel, statusKey,
  } = status;
  const statusStyleKey = resolveStatusStyleKey(statusKey);
  const canRegenerate = [status.canRegenerate, regenerateEnabled, typeof onRegenerate === 'function']
    .every(Boolean);
  const itemProjection = Object.assign({}, item);
  const processDisclosureLabel = '执行过程';
  const retryAction = conditionalValue(canRegenerate, (
    <Button aria-label="重试" icon={<ReloadOutlined />} onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      onRegenerate();
    }} size="small" type="text" style={{ color: themeTokens.textSecondary, paddingInline: 4 }}>
      重试
    </Button>
  ), null);

  return (
    <div className="flare-assistant-meta-row" style={{ flexDirection: 'column' }}>
      {showIdentity ? <MessageIdentityAvatar
        assistantName={assistantName}
        messageIdentityKey={item.messageIdentityKey}
        renderMessageAvatar={renderMessageAvatar}
        role="assistant"
        themeTokens={themeTokens}
      /> : null}
      <div
        className={`flare-thinking-panel is-${statusStyleKey}${conditionalValue(dimmed, ' is-dimmed', '')}${conditionalValue(isSettled, ' is-settled', '')}`}
        style={{
          width: '100%',
        }}
      >
        <Space orientation="vertical" size={6} style={{ width: '100%' }}>
          {!hasProcessLines ? <>
            <ProcessCompactSummary label={stageLabel} status={status} actions={retryAction} />
            {conditionalValue(Boolean(secondaryLabel), (
              <Text className="flare-thinking-card-tool" style={{ color: themeTokens.textSecondary }} type="secondary">
                {secondaryLabel}
              </Text>
            ), null)}
          </> : null}
          {hasProcessLines ? (
            <ThinkingProcessLog
              activityEntries={activityEntries}
              hasProcessLines={hasProcessLines}
              processDisclosureLabel={processDisclosureLabel}
              processSummary={processSummary}
              processStatus={status}
              processActions={retryAction}
              resourceEntries={resourceEntries}
              processCompleted={settlement.completed}
              shouldOpenProcess={shouldOpenProcess}
              themeTokens={themeTokens}
              visibleProcessLines={visibleProcessLines}
              workflowOperationActions={itemProjection.workflowOperationActions}
              workflowProcessEntries={workflowProcessEntries}
              onUICardAction={onUICardAction}
              onWorkflowOperationAction={onWorkflowOperationAction}
            />
          ) : null}
        </Space>
      </div>
    </div>
  );
}
